import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { CampaignAnalyticsService } from '@core/services/campaign-analytics.service';
import { IAnalyticsImage } from '@interfaces/deliverable-analytics.interface';
import { TranslateModule } from '@ngx-translate/core';
import { IconComponent } from '@shared/components/icon/icon.component';
import { IFindingImageZoomDialogData } from '../finding-card.interface';
import { FindingImageZoomDialogComponent } from '../finding-image-zoom-dialog/finding-image-zoom-dialog.component';

@Component({
  imports: [IconComponent, TranslateModule],
  selector: 'app-finding-image',
  templateUrl: './finding-image.component.html',
})
export class FindingImageComponent {
  public readonly findingId = input.required<string>();
  public readonly analyticsImage = input<IAnalyticsImage | null | undefined>(null);
  public readonly canLoad = input<boolean>(false);
  // Set in export mode: the image is read through the public export route.
  public readonly campaignExportId = input<string | null>(null);
  public readonly aspectRatio = input<string>('16 / 9');
  public readonly alt = input<string>('');

  public readonly objectUrl = signal<string | null>(null);

  private readonly campaignAnalyticsService = inject(CampaignAnalyticsService);
  private readonly dialog = inject(MatDialog);

  // Keyed by the storage key: a replaced image is re-fetched, a re-created finding object is not.
  private readonly imageKey = computed(() => (this.canLoad() ? (this.analyticsImage()?.key ?? null) : null));

  constructor() {
    effect((onCleanup) => {
      const findingId = this.findingId();
      const campaignExportId = this.campaignExportId();

      this.objectUrl.set(null);

      if (!this.imageKey()) {
        return;
      }

      let objectUrl: string | null = null;

      const subscription = this.campaignAnalyticsService
        .getFindingAnalyticsImage(findingId, campaignExportId)
        .subscribe({
          error: () => this.objectUrl.set(null),
          next: (blob) => {
            objectUrl = URL.createObjectURL(blob);
            this.objectUrl.set(objectUrl);
          },
        });

      onCleanup(() => {
        subscription.unsubscribe();

        if (objectUrl) {
          URL.revokeObjectURL(objectUrl);
        }
      });
    });
  }

  public onImageError(): void {
    this.objectUrl.set(null);
  }

  public onEnlarge(imageUrl: string): void {
    const data: IFindingImageZoomDialogData = { alt: this.alt(), imageUrl };

    this.dialog.open(FindingImageZoomDialogComponent, {
      data,
      maxHeight: '90vh',
      maxWidth: '90vw',
    });
  }
}
